import { gql, useQuery } from "@apollo/client";
import {
  useIdentifyToFullStory,
  useInstallFullStory,
} from "@zapier/react-fullstory";
import { useEffect, useState } from "react";

import { FullStoryUserQuery } from "./index.generated";

const fullStoryUserQuery = gql`
  query FullStoryUser($accountId: ID!) {
    account(id: $accountId) {
      id
      plan {
        id
        name
      }
    }
    currentUser {
      email
      fullName
      id
      roles {
        accountId
        role
      }
    }
  }
`;

const AUTOMATION_EMAIL_ADDRESSES = [
  "user@example.com",
  "end-to-end@zapier-test.com",
];

interface Props {
  accountId: number | undefined;
  disableFullStory?: boolean;
}

interface User {
  id: string;
  fullName: string;
  email: string;
  [key: string]: any; // any other fields are acceptable here
}
interface FullStoryProps {
  user: User;
}

const FullStoryContainer = ({ user }: FullStoryProps) => {
  useIdentifyToFullStory(user);

  return null;
};

export const FullStory = ({ accountId, disableFullStory }: Props) => {
  const [fullStoryUser, setFullStoryUser] = useState<User | undefined>();
  // Fetch user and account info for FullStory
  const { loading, error, data } = useQuery<FullStoryUserQuery>(
    fullStoryUserQuery,
    {
      variables: { accountId },
      skip: !accountId || disableFullStory,
    }
  );

  const user = data?.currentUser;
  const account = data?.account;

  useEffect(() => {
    if (user && account) {
      const role = user.roles.find(
        (r) => r.accountId === account.id?.toString()
      );

      setFullStoryUser({
        displayName: user.fullName,
        email: user.email,
        fullName: user.fullName,
        id: user.id,
        planId_str: account.plan?.id,
        planName_str: account.plan?.name,
        role_str: role?.role || "owner",
      });
    }
    // only rerun effect if user or account changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, user?.id]);

  const shouldRecordFullStory =
    !disableFullStory &&
    // make sure we are loaded with no errors
    !loading &&
    !error &&
    // Do not track zapier staff
    !user?.email?.includes("@zapier.com") &&
    // Do not track automation accounts
    user?.email &&
    !AUTOMATION_EMAIL_ADDRESSES.includes(user?.email) &&
    // Do not track if rendering in server
    typeof window !== "undefined";

  // only initialize FullStory if shouldRecordFullStory is truthy
  useInstallFullStory({ isDisabled: !shouldRecordFullStory });

  if (shouldRecordFullStory && !!fullStoryUser) {
    return <FullStoryContainer user={fullStoryUser} />;
  }

  return null;
};
